import { useEffect, useState } from 'react';
import { api, type Bench, type Defaults, type ModelInfo } from '../lib/api';
import { ModelPicker } from './ModelPicker';

// ---------------------------------------------------------------------------
// The workspace's models (204): what each bench starts from before anyone
// chooses, saved with the other defaults. The text model is the one the
// director writes with — its rows say the price per thousand tokens and
// how much cheaper a cached read is — and the four media benches take
// theirs here too. Reachable in both modes, on Settings.
// ---------------------------------------------------------------------------

const BENCHES: Array<{ bench: Bench; label: string; hint: string }> = [
  { bench: 'text', label: 'The director', hint: 'Storylines, hooks, research and every proposal are written with this model. Priced per thousand tokens; a cached read is cheaper.' },
  { bench: 'image', label: 'Pictures', hint: 'What the Image tab and the Assets bench start on.' },
  { bench: 'edit', label: 'Edits', hint: 'What a redraw or a combine starts on.' },
  { bench: 'music', label: 'Music', hint: 'What a bed is composed on.' },
  { bench: 'video', label: 'Shots', hint: 'What a generated shot is made on.' },
];

export function WorkspaceModels({ value, onChange, onOpenCatalogue }: { value: Defaults; onChange: (d: Defaults) => void; onOpenCatalogue?: () => void }) {
  const [lists, setLists] = useState<Partial<Record<Bench, ModelInfo[]>>>({});
  const [busy, setBusy] = useState<Bench | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all(BENCHES.map((b) => api.models(b.bench).then((m) => [b.bench, m] as const)))
      .then((pairs) => { if (live) setLists(Object.fromEntries(pairs)); })
      .catch((e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, []);

  async function choose(bench: Bench, m: ModelInfo) {
    setBusy(bench); setError(null);
    try {
      const r = await api.saveDefaults({ ...value, models: { ...(value.models ?? {}), [bench]: m.id } });
      onChange(r.defaults);
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <section className="card" data-tool="workspace-models">
      <h3>Models</h3>
      <p className="sub-dim" style={{ margin: 0 }}>What each bench starts on for everyone in this workspace. A creator can still pick another on the bench; their browser remembers it.{onOpenCatalogue ? <> <button type="button" className="linkish" onClick={onOpenCatalogue}>Read about every model</button>.</> : null}</p>
      {error && <p className="err inline" role="alert">{error}</p>}
      <dl className="kv models">
        {BENCHES.map(({ bench, label, hint }) => {
          const list = lists[bench] ?? null;
          const chosenId = value.models?.[bench];
          const chosen = list?.find((m) => m.id === chosenId) ?? list?.find((m) => m.workspaceDefault) ?? list?.find((m) => m.houseDefault) ?? null;
          return (
            <div key={bench} className="models-row">
              <dt title={hint}>{label}{busy === bench ? <span className="sub-dim"> · saving…</span> : null}</dt>
              <dd>
                <ModelPicker bench={bench} models={list} value={chosen} onChange={(m) => void choose(bench, m)} />
                {bench === 'text' && chosen?.options.tokens && (
                  <span className="sub-dim">{chosen.options.tokens.input.toLocaleString()} cr / 1k tokens in · {chosen.options.tokens.output.toLocaleString()} out{chosen.cacheDiscount ? ` · cached reads ${Math.round(chosen.cacheDiscount * 100)}% off` : ''}</span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
